/****************************************************
 * Module Name:  addLot.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import { Box, Button, Container, Tooltip } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Constants from "../../../utility/constants";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import "./../../../style/modalstyle.css";
import LotCreationProxy from "../../../utility/proxy/manager/manufacturingProxy/lotCreationProxy";
import Utility from "../../../utility/utility";
import CommonSnackbar from "../../common/commonSnackBar";
import { LoadingButton } from "@mui/lab";
import logo from '../../../images/pansuriya.jpg'
import { DataGridPro, GridToolbar, GridEditInputCell } from "@mui/x-data-grid-pro";
import EditIcon from '@mui/icons-material/Edit';
import LotDetailModal from "./lotDetailModal";
import { HiMiniArrowRightEndOnRectangle } from "react-icons/hi2";

const AddLot = ({ handleClose, show, loading, handleSave, handleValidation, }) => {
    const { apiRef, handleCellClick } = Utility.useSingleClickEdit();
    const [data, setData] = useState([]);
    const [detailLoader, setDetailLoader] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(true);
    const [filterValues, setFilterValues] = useState({});
    const [selectionModel, setSelectionModel] = useState([]);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [showSnackbar, setShowSnackbar] = useState(false);

    const lotDetailPermission = Constants.USER_PERMISSION.filter(iPermission =>
        iPermission.permissionid === Constants.PERMISSIONS.LOTDETAIL)
    const [showLotDetailsModal, setShowLotDetailsModal] = useState(false);
    const [lotid, setLotId] = useState("");
    const [columnNames, setColumnNames] = React.useState([]);

    useEffect(() => {
        handleSearch();
    }, []);

    useEffect(() => {
        let flag = true;
        if (selectionModel.length > 0) {
            data.forEach(iData => {
                if (selectionModel.includes(iData.lotcreationid)) {
                    if (iData.newpcs && iData.newppc && iData.newcarats && iData.newpcs !== 0 && iData.newppc !== 0 && iData.newcarats !== 0 &&
                        (iData.pendingpcs === 0 ? iData.pendingpcs === 0 && iData.pendingcarats === 0 : true) &&
                        (iData.pendingcarats === 0 ? iData.pendingpcs === 0 && iData.pendingcarats === 0 : true)
                    ) {
                        flag = false;
                    } else {
                        flag = true;
                    }
                }
            });
            setDisableSubmit(flag);
        } else {
            setDisableSubmit(flag);
        }
    }, [selectionModel, data]);

    const handleSearch = () => {
        setDetailLoader(true);
        LotCreationProxy.GetAllLotCreationByPendingWeight((responseData) => {
            if (responseData) {

                const uniqueFilterValues = {
                    companyname: Utility.getDistinctOptions(responseData, "companyname"),
                };
                setFilterValues(uniqueFilterValues);

                setData(responseData.map(iData => ({
                    ...iData,
                    finalpendingpcs: iData.pendingpcs,
                    finalpendingcarats: iData.pendingcarats,
                    finalpendingprice: iData.pendingprice,
                    newpcs: 0,
                    newcarats: 0,
                    newppc: 0
                })));
                prepareColumns(uniqueFilterValues);
                setDetailLoader(false);

            }
        });
    };

    const prepareColumns = (uniqueFilterValues) => {
        const columnNames = columnsWithoutPermission.map(woPermissionColumn => {
            let guids = [];

            // Determine which GUIDs (permissions) should be added based on the column field
            switch (woPermissionColumn.field) {
                case "companyname":
                    guids = [
                        Constants.COLUMN_PERMISSION.MFG_LOT_CRTN_CREATEDAT,
                        Constants.COLUMN_PERMISSION.MFG_LOT_CRTN_DATE_RECEIVED,
                        Constants.COLUMN_PERMISSION.MFG_LOT_CRTN_PCS,
                        Constants.COLUMN_PERMISSION.MFG_LOT_CRTN_CARATS,
                        Constants.COLUMN_PERMISSION.MFG_LOT_CRTN_PRICE,
                        Constants.COLUMN_PERMISSION.MFG_LOT_CRTN_TOTAL,
                        Constants.COLUMN_PERMISSION.MFG_LOT_CRTN_PENDINGPCS,
                        Constants.COLUMN_PERMISSION.MFG_LOT_CRTN_PENDING_CARATS,
                        Constants.COLUMN_PERMISSION.MFG_LOT_CRTN_PENDINGPRICE,
                    ];
                    break;
                default:
                    guids = [];
            }

            // Retrieve valid permission columns
            return fetchColumnDataFromGuid(guids, woPermissionColumn, columnsWithPermission, uniqueFilterValues);

        }).flat(); // Flatten the array

        // Update state with the prepared columns
        setColumnNames(columnNames);
    };

    const fetchColumnDataFromGuid = (guids, currentColumn, columnsWithPermission, uniqueFilterValues) => {
        const permissionColumns = guids
            .map(id => Constants.USER_COLUMN_PERMISSION.find(permission => permission === id)) // Match permissions
            .filter(Boolean) // Remove undefined values (invalid permissions)
            .map(guid => {
                const columnData = columnsWithPermission[guid]; // Get column details

                // If the columnData is missing or an empty object, skip adding it
                if (!columnData || Object.keys(columnData).length === 0) {
                    return null;
                }

                // Retrieve unique filter values for the column, defaulting to an empty array
                const filterData = uniqueFilterValues[columnData.field] || [];

                return {
                    ...columnData,  // Copy all existing column properties
                    valueOptions: filterData,  // Assign filter values
                };
            })
            .filter(Boolean); // Remove any null values from the list

        // Modify the original column before adding it to the list
        const modifiedColumn = {
            ...currentColumn,  // Retain original column properties
            valueOptions: uniqueFilterValues[currentColumn.field] || [], // Assign filter values
        };

        // Return an array with the modified column and any valid permission columns
        return [modifiedColumn, ...permissionColumns];
    };

    const handleCreateLot = () => {
        setDisableSubmit(true);
        let arr = [];

        if (selectionModel.length > 0) {
            data.forEach(iData => {
                if (selectionModel.includes(iData.lotcreationid)) {
                    let obj = {
                        lotcreationid: iData.lotcreationid,
                        newcarats: iData.newcarats,
                        newppc: iData.newppc,
                        newpcs: iData.newpcs
                    }
                    arr.push(obj);
                }
            });

            if (handleSave) {
                if (handleValidation) {
                    if (handleValidation(arr)) {
                        handleSave(arr);
                    } else {
                        setSnackbarState(true, Constants.APiFailErrorMessage, "error");
                    }
                } else {
                    handleSave(arr);
                }
            }
        }
    };

    const getChangedFields = (newRow, oldRow) => {
        const changedFields = [];
        Object.keys(newRow).forEach(field => {
            if (newRow[field] !== oldRow[field]) {
                changedFields.push({
                    id: newRow.lotcreationid,
                    field: field,
                    value: newRow[field]
                });
            }
        });

        return changedFields;
    };

    const handleProcessRowUpdateError = (error) => {
        // console.error(error);
    };

    const processRowUpdate = (newRow, oldRow) => {

        const changedFields = getChangedFields(newRow, oldRow);
        if (changedFields.length > 0) {
            const { id, field, value } = changedFields[0];
            let updatedRecord;
            const updatedRow = data.map(iData => {
                if (iData.lotcreationid === id) {
                    if (field === "newcarats") {
                        const carats = value ? parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS)) : 0;
                        if (carats <= 0 || carats > iData.finalpendingcarats) {
                            setSnackbarState(true, carats <= 0 ? `Carats can't be 0 or less than 0` :
                                `Carats can't be greater than ${iData.finalpendingcarats}`, "warning");
                            return iData;
                        }

                        let tempPendingCarats = parseFloat((iData.finalpendingcarats - carats).toFixed(Constants.DEFAULT_NO_OF_DIGITS));
                        let tempPendingAmount = tempPendingCarats === 0 ? 0 :
                            parseFloat((((iData.finalpendingcarats * iData.finalpendingprice) - (carats * iData.newppc)) / tempPendingCarats).toFixed(2));

                        updatedRecord = {
                            ...iData,
                            [field]: carats,
                            pendingcarats: tempPendingCarats,
                            pendingprice: tempPendingAmount
                        }
                        return updatedRecord;
                    } else if (field === "newppc") {
                        const ppc = value ? parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS)) : 0;
                        if (ppc <= 0) {
                            setSnackbarState(true, `PPC can't be 0 or less than 0`, "warning");
                            return iData;
                        }

                        let tempPendingCarats = parseFloat((iData.finalpendingcarats - iData.newcarats).toFixed(Constants.DEFAULT_NO_OF_DIGITS));
                        let tempPendingAmount = tempPendingCarats === 0 ? 0 :
                            parseFloat((((iData.finalpendingcarats * iData.finalpendingprice) - (iData.newcarats * ppc)) / tempPendingCarats).toFixed(2));

                        updatedRecord = {
                            ...iData,
                            [field]: ppc,
                            pendingcarats: tempPendingCarats,
                            pendingprice: tempPendingAmount
                        }
                        return updatedRecord;
                    } else {
                        const pcs = value ? isNaN(parseInt(value)) ? 0 : parseInt(value) : 0;
                        if (pcs <= 0 || pcs > iData.finalpendingpcs) {
                            setSnackbarState(true, pcs <= 0 ? `Pcs can't be 0 or less than 0` :
                                `Pcs can't be greater than ${iData.finalpendingpcs}`, "warning");
                            return iData;
                        }

                        updatedRecord = {
                            ...iData,
                            [field]: pcs,
                            pendingpcs: iData.finalpendingpcs - pcs
                        }
                        return updatedRecord;
                    }
                }
                return iData;
            });

            if (updatedRecord)
                setData(updatedRow);

            return updatedRecord || oldRow;
        } else {
            // No changes detected, return the newRow to trigger exit edit mode
            return { ...newRow };
        }
    };

    const setSnackbarState = (show, message, variant) => {
        setShowSnackbar(show);
        setSnackbarMessage(message);
        setSnackbarVariant(variant);
    };

    const handleCopyPendingValues = (rowId) => {
        setData((prev) =>
            prev.map((row) =>
                row.lotcreationid === rowId
                    ? {
                        ...row,
                        newpcs: row.finalpendingpcs,
                        newcarats: row.finalpendingcarats,
                        newppc: row.finalpendingprice,
                        pendingcarats: 0,
                        pendingpcs: 0,
                        pendingprice: 0,
                    }
                    : row
            )
        );
    };


    const columnsWithPermission = {
        "25c97def-d08f-4d8c-a102-34030a9adea3": {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            width: 95,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalDate(params.value),
        },
        "87533f90-57b8-4c2b-ab39-e1aefe67e778": {
            field: "datereceived",
            headerName: "RECEIVED AT",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            }
        },
        "1a7ee27e-693c-45b0-8efe-f5a5df49a9a5": {
            field: "pcs",
            headerName: "INITIAL PCS",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: 'number',
        },
        "1ed2e58e-50c7-4229-bae3-bebb85e70722": {
            field: "carats",
            headerName: "INITIAL CTS",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: 'number',
        },
        "db5cef10-f033-4f81-8423-526453b57e5f": {
            field: "price",
            headerName: "INITIAL PPC",
            headerAlign: "center",
            align: "center",
            width: 90,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price),
            type: 'number',
        },
        "af9588fb-1eea-4665-8b49-c1da35bbdfc7": {
            field: "total",
            headerName: "INITIAL TOTAL",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total),
            type: 'number',
        },
        "242b96e4-098c-48ef-bbee-31263e0ffe12": {
            field: "pendingcarats",
            headerName: "PENDING CTS",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: 'number',
        },
        "f2b42be2-c5e7-4f96-8b82-463f138d4e3c": {
            field: "pendingpcs",
            headerName: "PENDING PCS",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: 'number',
        },
        "86636ce6-344b-4209-a456-c04133eeae1c": {
            field: "pendingprice",
            headerName: "PENDING PPC",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: 'number',
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.pendingprice),
        },
    };

    const columnsWithoutPermission = [
        {
            field: "lotcreationid",
            headerName: "LOT ID",
            headerAlign: "center",
            align: "center",
            width: 55,
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.isReadAllowed(lotDetailPermission) ?
                (<Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setShowLotDetailsModal(true);
                        setLotId(params.value)
                    }}
                >
                    {params.value}
                </Button>) : <div>{params.value}</div>,
        },
        {
            field: "lotintimationid",
            headerName: "LOT INT ID",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: 'number',
        },
        {
            field: "companyname",
            headerName: "COMPANY NAME",
            headerAlign: "center",
            valueOptions: filterValues ? filterValues.companyname : [],
            align: "center",
            width: 200,
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
        },
        {
            field: "movePending",
            headerName: "MOVE",
            headerAlign: "center",
            align: "center",
            width: 80,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Button
                    onClick={() => handleCopyPendingValues(params.row.lotcreationid)}
                    size="small"
                    color="primary"
                >
                    <HiMiniArrowRightEndOnRectangle size={23} />
                </Button>
            )
        },
        {
            field: "newpcs",
            headerName: "ENTER PCS",
            headerAlign: "center",
            type: "number",
            width: 120,
            editable: true,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value}
                    </span>
                    <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />
                </div>
            ),
            renderEditCell: (params) => {
                return (<Box ><GridEditInputCell {...params} /></Box>)
            },
            cellClassName: 'lavenderCell',
        },
        {
            field: "newcarats",
            headerName: "ENTER CTS",
            headerAlign: "center",
            type: "number",
            width: 120,
            editable: true,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value}
                    </span>
                    <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />
                </div>
            ),
            renderEditCell: (params) => {
                return (<Box ><GridEditInputCell {...params} /></Box>)
            },
            cellClassName: 'lavenderCell',
        },
        {
            field: "newppc",
            headerName: "ENTER PPC",
            headerAlign: "center",
            type: "number",
            width: 120,
            editable: true,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value ? parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS) : 0.00}
                    </span>
                    <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />
                </div>
            ),
            renderEditCell: (params) => {
                return (<Box ><GridEditInputCell {...params} /></Box>)
            },
            cellClassName: 'lavenderCell',
        },
    ];

    const rows = data;
    return (
        <div>
            <Modal backdrop="static" show={show} onHide={handleClose} dialogClassName="bhb" enforceFocus={false}>
                <CommonSnackbar
                    show={showSnackbar}
                    message={snackbarMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" :
                        snackbarVariant === "error" ? "Failed" : "Warning"}
                    handleClose={() => setShowSnackbar(false)}
                />

                {showLotDetailsModal ? (
                    <LotDetailModal
                        lotid={lotid}
                        handleClose={() => setShowLotDetailsModal(false)}
                    />
                ) : null}

                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;Add Lot Creation
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Container maxWidth="xxl">
                        <Box
                            sx={{
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeightBold,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeightBold,
                                },
                                height: "50vh",
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={true}
                                density="compact"
                                rowHeight={60}
                                loading={detailLoader}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    "& .lavenderCell": {
                                        color: "#080808",
                                        backgroundColor: "#D8BFD8",
                                    },
                                    "& .cursorNA": {
                                        color: "#080808",
                                        backgroundColor: "#D8BFD8",
                                        cursor: "not-allowed"
                                    },
                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeightBold,
                                }}
                                rows={rows}
                                columns={columnNames}
                                getRowId={(row) => row.lotcreationid}
                                checkboxSelection={true}
                                onRowSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
                                rowSelectionModel={selectionModel}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns id, the other columns will remain visible
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                slots={{ toolbar: GridToolbar }}
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                processRowUpdate={(newR, oldRow) => processRowUpdate(newR, oldRow)}
                                onProcessRowUpdateError={handleProcessRowUpdateError}
                                apiRef={apiRef}
                                onCellClick={handleCellClick}
                                editMode="cell"
                            />
                            <Box style={{ position: "absolute", top: 5, right: "270px" }}>
                                {detailLoader ? (
                                    <CircularProgress size="1rem" />
                                ) : (
                                    <Tooltip title="Reload List">
                                        <RefreshOutlinedIcon
                                            onClick={() => handleSearch()}
                                            color="primary"
                                            fontSize="medium"
                                            sx={{ cursor: "pointer", mr: 1 }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    </Container>
                </Modal.Body>
                <Modal.Footer>
                    <LoadingButton
                        loading={loading}
                        loadingPosition="start"
                        variant="contained"
                        component="label"
                        sx={{ mr: "15px" }}
                        startIcon={<AddBoxOutlinedIcon size="large" />}
                        disabled={disableSubmit}
                        onClick={handleCreateLot}
                    >
                        Create Lot
                    </LoadingButton>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleClose}
                        sx={{ mr: "15px" }}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AddLot;
